// TORQ brand — custom-styled dropdown replacing a native <select>'s
// open list (the browser's native option popup can't be restyled with
// CSS in any browser). The real <select> stays in the DOM, visually
// hidden but still focusable, so keyboard/screen-reader users keep the
// native experience; this drives a branded list for everyone else and
// keeps the real <select> in sync so existing form wiring (facets.js
// listens for `input` bubbling up from the form) keeps working
// unchanged.
class TorqSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.trigger = this.querySelector('.torq-select__trigger');
    this.list = this.querySelector('.torq-select__list');
    this.valueEl = this.querySelector('.torq-select__value');
    this.options = Array.from(this.querySelectorAll('.torq-select__option'));
    if (!this.select || !this.trigger || !this.list) return;

    this.trigger.addEventListener('click', () => this.toggle());

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.chooseOption(option));
    });

    this.list.addEventListener('keydown', this.onListKeydown.bind(this));

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.trigger.focus();
      }
    });

    // Keep the custom UI in sync if the real <select> changes some
    // other way (browser back/forward restoring form state, etc).
    this.select.addEventListener('change', () => this.syncFromSelect());
  }

  toggle() {
    if (this.list.hidden) this.open();
    else this.close();
  }

  open() {
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    const current = this.options.find((option) => option.getAttribute('aria-selected') === 'true');
    (current || this.options[0])?.focus();
  }

  close() {
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  chooseOption(option) {
    const value = option.dataset.value;
    if (this.select.value !== value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.syncFromSelect();
    this.close();
    this.trigger.focus();
  }

  syncFromSelect() {
    const selected = this.select.options[this.select.selectedIndex];
    if (this.valueEl && selected) this.valueEl.textContent = selected.textContent.trim();
    this.options.forEach((option) => {
      option.setAttribute('aria-selected', option.dataset.value === this.select.value ? 'true' : 'false');
    });
  }

  onListKeydown(event) {
    const currentIndex = this.options.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = this.options[currentIndex + 1] || this.options[0];
      next.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = this.options[currentIndex - 1] || this.options[this.options.length - 1];
      prev.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (document.activeElement && this.options.includes(document.activeElement)) {
        this.chooseOption(document.activeElement);
      }
    }
  }
}

customElements.define('torq-select', TorqSelect);
